import moment from "moment";
import { baseUrl } from "../../../baseUrl";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { fetchWithAuthHandling } from "../../../authHandling";
import { formatPhoneNumber } from "../../../utils/formatPhoneNumber";
import {
  Call,
  Timer,
  Close,
  Phone,
  EventNote,
  Link as LinkIcon,
  SentimentSatisfied,
} from "@mui/icons-material";
import {
  Box,
  Link,
  Stack,
  Paper,
  Button,
  Dialog,
  Divider,
  Typography,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";

interface CallsModalProps {
  openDialog: boolean;
  callId: string | null;
  buyerId: string;
}

export const CallsModal = ({
  callId,
  openDialog,
  buyerId,
}: CallsModalProps) => {
  const navigate = useNavigate();
  const handleCloseDialog = () => {
    navigate({ to: `/buyers/${buyerId}/calls` })
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["callDetails", callId],
    queryFn: async () => {
      if (!callId) return null;
      const response = await fetchWithAuthHandling(
        `${baseUrl}/buyers/${buyerId}/calls/${callId}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      return response.json();
    },
    retry: false,
  });

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="md"
      aria-labelledby="call-summary-dialog-title"
    >
      <DialogTitle
        id="call-summary-dialog-title"
        sx={{
          display: "flex",
          fontWeight: "bold",
          alignItems: "center",
          padding: "16px 24px",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Call />
          Call Details
        </Box>
        <Button onClick={handleCloseDialog} sx={{ minWidth: "auto", color: "#888" }}>
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Typography variant="body1" color="error" sx={{ textAlign: "center", py: 4 }}>
            Failed to load call details. Please try again.
          </Typography>
        ) : (
          <>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 1 }}>
                Call Summary
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {data?.callSummary || "No summary available"}
              </Typography>
            </Paper>

            <Divider />

            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 1 }}>
                Call Information
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone sx={{ fontSize: "18px", mr: 1, color: "gray" }} />
                  <Link
                    href={`tel:${data?.phoneNumber}`}
                    sx={{ textDecoration: "none", color: "#1976d2", "&:hover": { textDecoration: "underline" } }}
                  >
                    <Box sx={{ fontSize: "14px" }}>
                      <strong style={{ color: "white" }}>Phone Number:</strong> {data?.phoneNumber ? formatPhoneNumber(data?.phoneNumber) : "N/A"}
                    </Box>
                  </Link>
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>First Name:</strong> {data?.firstName ? data?.firstName : "N/A"}
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Email:</strong> {data?.emailAddress ? data?.emailAddress : "N/A"}
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Agent:</strong> {data?.agentName ? data?.agentName : "N/A"}
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Status:</strong> {data?.callStatus ? data?.callStatus : "N/A"}
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Vendor:</strong> {data?.vendor ? data?.vendor : "N/A"}
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Disconnection Reason:</strong> {data?.disconnectionReason ? data?.disconnectionReason : "N/A"}
                </Box>
              </Stack>
            </Paper>

            <Divider />

            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 1 }}>
                Call Timing
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EventNote sx={{ fontSize: "18px", mr: 1, color: "gray" }} />
                  <div>
                    <Box sx={{ fontSize: "14px" }}>
                      <strong>Start:</strong> {data?.start ? moment(data?.start).format("YYYY-MM-DD hh:mm a") : "N/A"}
                    </Box>
                  </div>
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>End:</strong> {data?.end ? moment(data?.end).format("YYYY-MM-DD hh:mm a") : "N/A"}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Timer sx={{ fontSize: "18px", mr: 1, color: "gray" }} />
                  <div>
                    <Box sx={{ fontSize: "14px" }}>
                      <strong>Duration:</strong> {data?.duration ? Math.round((data?.duration || 0) / 1000) : "N/A"} sec
                    </Box>
                  </div>
                </Box>
              </Stack>
            </Paper>

            <Divider />

            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 1 }}>
                Additional Details
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Created At:</strong> {data?.createdAt ? moment(data?.createdAt).format("YYYY-MM-DD hh:mm a") : "N/A"}
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Updated At:</strong> {data?.updatedAt ? moment(data?.updatedAt).format("YYYY-MM-DD hh:mm a") : "N/A"}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
                  <SentimentSatisfied sx={{ fontSize: "18px", mr: 1, color: "gray" }} /><div>
                    <strong>Sentiment:</strong> {data?.sentiment ? data?.sentiment : "N/A"}</div>
                </Box>
                <Box sx={{ fontSize: "14px" }}>
                  <strong>Action Timestamp:</strong> {data?.actionTimestamp ? moment(data?.actionTimestamp).format("YYYY-MM-DD hh:mm a") : "N/A"}
                </Box>
                {data?.publicLogUrl && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LinkIcon sx={{ fontSize: "18px", mr: 1, color: "gray" }} />
                    <Link href={data.publicLogUrl} target="_blank" rel="noopener noreferrer">
                      View Public Log
                    </Link>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Divider />

            {data?.recordingUrl && (
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="700">Call Recording:</Typography>

                {data.recordingUrl ? (
                  // biome-ignore lint/a11y/useMediaCaption: <explanation>
                  <audio controls style={{ width: "100%", marginTop: "10px" }}>
                    <source src={data.recordingUrl} type="audio/mpeg" />

                    {data.transcriptUrl && (
                      <track
                        kind="captions"
                        src={data.transcriptUrl}
                        label="English Captions"
                        default
                      />
                    )}

                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <Typography variant="body2" color="error">Recording not available.</Typography>
                )}
                {data.recordingUrl && (
                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      href={data.recordingUrl}
                      download
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <LinkIcon />
                      Download
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
          </>
        )}
      </DialogContent>
    </Dialog >
  );
};
